import React, { useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  User,
  FileText,
  Eye,
  Pencil,
  Trash2,
  Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SocialFrequencyDisplay } from '../common/SocialFrequencyDisplay';
import { SocialIcon, type SocialPlatform, normalizePlatformName } from '../icons/SocialIcon';
import { PostPreview } from '../calendar/PostPreview';
import { CreatePostModal } from '../posts/CreatePostModal';
import { ErrorHandlingMedia } from '../common/ErrorHandlingMedia';
import { normalizeMedia } from '../../utils/mediaUtils';
import { MediaStructureDebug } from '../debug/MediaStructureDebug';
import type { PostingFrequency } from '../common/SocialFrequencyDisplay';
import type { Post } from '../../types/calendar';

interface SocialNetwork {
  platform: string;
  pertinence: 'Primary' | 'Secondary' | 'Tertiary';
  frequency: PostingFrequency;
}

interface RecentPost {
  postId: string;
  postTitle: string;
  postContent: string;
  postPlatform: string;
  postStatus: string;
  postMediaUrl: string | null;
  postCreatedAt: string;
  postUpdatedAt: string;
  postScheduledAt?: string | null;
  source?: string;
}

interface PersonaProps {
  id: string;
  name: string;
  jobTitle: string;
  tags: string[];
  ageRange: string;
  age: number;
  languages: string[];
  socialNetworks: SocialNetwork[];
  problems: string | string[];
  goals: string | string[];
  motivations: string | string[];
  challenges: string | string[];
  formatLength: string[];
  avatarUrl?: string;
  location?: string;
  income?: string;
  education?: string;
  relationship?: string;
  buying_behavior?: string;
  brand_affinity?: string;
  quote?: string;
  brandId: string;
  brandName?: string;
  brandLogo?: string;
  recentPosts?: RecentPost[];
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const PersonaCard: React.FC<PersonaProps> = ({
  id,
  name,
  jobTitle,
  tags,
  ageRange,
  age,
  languages,
  socialNetworks,
  problems,
  goals,
  motivations,
  challenges,
  formatLength,
  avatarUrl,
  location,
  income,
  education,
  relationship,
  buying_behavior,
  brand_affinity,
  quote,
  brandId,
  brandName,
  brandLogo,
  recentPosts,
  onClick,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [avatarError, setAvatarError] = useState(false);
  const [brandLogoError, setBrandLogoError] = useState(false);
  const [hoveredPost, setHoveredPost] = useState<RecentPost | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [previewHeight, setPreviewHeight] = useState<number>(0);
  const previewRef = useRef<HTMLDivElement>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);

  const getLanguageEmoji = (lang: string) => {
    switch (lang.toLowerCase()) {
      case 'en':
        return '🇬🇧';
      case 'fr':
        return '🇫🇷';
      case 'es':
        return '🇪🇸';
      default:
        return '🌐';
    }
  };

  // Transform social networks to match SocialFrequencyDisplay format
  const networks = (socialNetworks || [])
    .map(network => {
      // Handle if network is a JSON string
      if (typeof network === 'string') {
        try {
          const parsed = JSON.parse(network);
          return {
            platform: parsed.platform?.toLowerCase() || '',
            frequency: parsed.frequency
          };
        } catch (error) {
          console.error('Error parsing socialNetwork JSON:', error);
          return null;
        }
      }
      // Handle if network is already an object
      if (typeof network === 'object' && network.platform) {
        return {
          platform: network.platform?.toLowerCase() || '',
          frequency: network.frequency
        };
      }
      return null;
    })
    .filter(network => network !== null);

  // Helper function to ensure consistent display of string or string[] fields
  const formatField = (value: string | string[] | undefined): string => {
    if (!value) return '';
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent card click when clicking action buttons
    if ((e.target as HTMLElement).closest('button')) {
      e.stopPropagation();
      return;
    }
    onClick?.();
  };

  // Helper function to map RecentPost to PostPreview Post type
  const mapRecentPostToPost = (post: RecentPost): Post => {
    // Convert postMediaUrl string to proper media object
    let media = null;
    if (post.postMediaUrl && post.postMediaUrl.trim() !== '') {
      media = { 
        url: post.postMediaUrl, 
        type: 'image' as const // Default to image, could be improved with URL analysis
      };
    }

    return {
      id: post.postId,
      brandName: brandName || '',
      brandLogo: brandLogo || '',
      content: post.postContent,
      format: 'regular', // fallback, could be improved if info available
      media,
      platforms: [post.postPlatform],
      status: post.postStatus as any,
      scheduledAt: post.postScheduledAt || null,
      publishedAt: null, // Add missing publishedAt property
      createdAt: post.postCreatedAt,
      updatedAt: post.postUpdatedAt,
      brandId: brandId,
    };
  };

  useLayoutEffect(() => {
    if (previewRef.current) {
      setPreviewHeight(previewRef.current.getBoundingClientRect().height);
    }
  }, [hoveredPost]);

  const getOptimalPosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const cardWidth = Math.min(800, viewportWidth * 0.9);
    const margin = 20;
    let x = mousePosition.x + 10;
    let y = mousePosition.y - 100;
    if (x + cardWidth + margin > viewportWidth) {
      x = mousePosition.x - cardWidth - 10;
    }
    if (x < margin) {
      x = margin;
    }
    // Use the real measured height
    if (y + previewHeight + margin > viewportHeight) {
      y = viewportHeight - previewHeight - margin;
    }
    if (y < margin) {
      y = margin;
    }
    return { x, y };
  };

  const handlePostClick = (postId: string) => {
    navigate(`/posts/${postId}/edit`);
  };

  const handleCreatePost = () => {
    setIsCreatePostModalOpen(true);
  };

  const handleCreatePostWithAI = () => {
    // Extract platform names from socialNetworks for preselection
    const recommendedPlatforms = networks
      .map(network => network.platform)
      .filter(platform => platform); // Remove any empty platforms
    
    console.log('PersonaCard - AI: Extracted platforms from networks:', networks);
    console.log('PersonaCard - AI: Recommended platforms to send:', recommendedPlatforms);
    
    setIsCreatePostModalOpen(false);
    navigate('/content-creation', { 
      state: { 
        selectedBrandId: brandId,
        selectedPersonaId: id,
        returnToPersonas: true,
        recommendedPlatforms: recommendedPlatforms
      } 
    });
  };

  const handleCreatePostManually = () => {
    // Extract platform names from socialNetworks for preselection
    const recommendedPlatforms = networks
      .map(network => network.platform)
      .filter(platform => platform); // Remove any empty platforms
    
    console.log('PersonaCard - Manual: Extracted platforms from networks:', networks);
    console.log('PersonaCard - Manual: Recommended platforms to send:', recommendedPlatforms);
    
    setIsCreatePostModalOpen(false);
    navigate('/content-creation', { 
      state: { 
        selectedBrandId: brandId,
        selectedPersonaId: id,
        createManually: true,
        returnToPersonas: true,
        recommendedPlatforms: recommendedPlatforms
      } 
    });
  };

  return (
    <div 
      className="bg-white rounded-xl border border-black shadow-lg hover:shadow-xl transition-shadow p-6 cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex items-start gap-6 mb-6">
        <div className="relative w-32 h-32 flex-shrink-0">
          {/* Avatar container */}
          <div className="w-full h-full aspect-square rounded-lg border border-black bg-primary-100 flex items-center justify-center overflow-hidden">
            {avatarUrl && !avatarError ? (
              <img 
                src={avatarUrl} 
                alt={`${name}'s avatar`} 
                className="w-full h-full object-cover"
                onError={() => setAvatarError(true)}
              />
            ) : (
              <User className="w-16 h-16 text-primary-600" />
            )}
          </div>
          
          {/* Brand logo positioned outside the avatar container */}
          {brandLogo && !brandLogoError && (
            <div className="absolute -top-2 -right-3 w-8 h-8 bg-white border border-black rounded-md shadow-sm flex items-center justify-center overflow-hidden z-10">
              <img 
                src={brandLogo} 
                alt={brandName || 'Brand logo'} 
                className="w-full h-full object-contain p-0.5"
                onError={() => setBrandLogoError(true)}
                title={brandName}
              />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Top row: name + languages (left) and action buttons (right) */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <h3 className="text-xl font-semibold text-gray-900 truncate">
                {name}
              </h3>
              <div className="flex gap-1 flex-shrink-0">
                {(languages || []).map((lang, index) => (
                  <span key={index} className="text-sm">
                    {getLanguageEmoji(lang)}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0 self-start">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            className="p-2 rounded-lg hover:bg-purple-100 transition-colors group relative"
            title={t('common.view', 'View')}
          >
            <Eye className="w-4 h-4 text-primary-600 group-hover:text-primary-700" />
            {/* Custom tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
              {t('common.view', 'Voir')}
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-primary-100"></div>
            </div>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            className="p-2 rounded-lg hover:bg-purple-100 transition-colors group relative"
            title={t('common.edit', 'Edit')}
          >
            <Pencil className="w-4 h-4 text-primary-600 group-hover:text-primary-700" />
            {/* Custom tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
              {t('common.edit', 'Modifier')}
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-primary-100"></div>
            </div>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            className="p-2 rounded-lg hover:bg-red-100 transition-colors group relative"
            title={t('common.delete', 'Delete')}
          >
            <Trash2 className="w-4 h-4 text-red-500 group-hover:text-red-700" />
            {/* Custom tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
              {t('common.delete', 'Supprimer')}
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-red-100"></div>
            </div>
          </button>
            </div>
          </div>

          {/* Second row: job title, age and quote spanning full width */}
          <div className="mt-1 min-w-0">
            <div className="text-sm text-gray-600">
              <div>{jobTitle}</div>
              <div className="text-gray-500">{t('personas.form.age')}: {age} {t('common.yearsOld', 'years old')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-width quote under the header (spans entire card, incl. avatar column) */}
      {quote && (
        <div className="-mt-4 mb-6">
          <div className="flex">
            <div className="w-1 bg-primary-600 mr-2"></div>
            <p className="text-sm italic text-gray-700 break-words w-full">"{quote}"</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Profile Information Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">{t('personas.form.tabs.profile', 'Profile')}</h4>
          
          {/* Demographic info */}
          <div className="flex flex-wrap gap-2 mb-3">
            {location && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs flex items-center">
                <span className="mr-1">📍</span> {location}
              </span>
            )}
            {income && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs flex items-center">
                <span className="mr-1">💰</span> {income}
              </span>
            )}
            {education && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs flex items-center">
                <span className="mr-1">🎓</span> {education}
              </span>
            )}
            {relationship && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs flex items-center">
                <span className="mr-1">👥</span> {relationship}
              </span>
            )}
          </div>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {(tags || []).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-primary-50 text-primary-700 rounded-lg text-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

                {/* Social Networks */}
        {networks.length > 0 && (
          <SocialFrequencyDisplay
            networks={networks}
            showAddButton={false}
          />
        )}

        {/* Recent Posts section */}
        <>
          <hr className="my-4 border-t border-gray-200" />
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-900">
                {t('brands.posts', 'Posts')} {recentPosts && recentPosts.length > 0 ? `(${recentPosts.length})` : ''}
              </h4>
              <button
                onClick={handleCreatePost}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                {t('posts.createPost', 'Créer un post')}
              </button>
            </div>
            {recentPosts && recentPosts.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {recentPosts.slice(0, 3).map((post) => (
                  <div
                    key={post.postId}
                    className="relative group cursor-pointer"
                    title={`${post.postContent.substring(0, 50)}...`}
                    onClick={() => handlePostClick(post.postId)}
                    onMouseEnter={e => {
                      setHoveredPost(post);
                      setMousePosition({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={e => {
                      setMousePosition({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseLeave={() => setHoveredPost(null)}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                      {(() => {
                        // Convert to proper media object first, then normalize
                        let mediaObj = null;
                        if (post.postMediaUrl && post.postMediaUrl.trim() !== '') {
                          mediaObj = { 
                            url: post.postMediaUrl, 
                            type: 'image' as const 
                          };
                        }
                        const normalizedMedia = normalizeMedia(mediaObj);
                        return normalizedMedia ? (
                          <ErrorHandlingMedia
                            src={normalizedMedia.url}
                            alt={post.postTitle}
                            type={normalizedMedia.type}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            controls={false}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <span className="text-xs">📝</span>
                          </div>
                        );
                      })()}
                      
                      {/* Platform icon overlay */}
                      <div className="absolute top-1 right-1">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-1">
                          <SocialIcon platform={normalizePlatformName(post.postPlatform) as SocialPlatform} size={12} />
                        </div>
                      </div>
                      
                      {/* Media format indicator */}
                      {(() => {
                        // Convert to proper media object first, then normalize
                        let mediaObj = null;
                        if (post.postMediaUrl && post.postMediaUrl.trim() !== '') {
                          mediaObj = { 
                            url: post.postMediaUrl, 
                            type: 'image' as const 
                          };
                        }
                        const normalizedMedia = normalizeMedia(mediaObj);
                        return normalizedMedia && (
                          <div className="absolute bottom-1 right-1 bg-white text-gray-800 text-xs px-1 py-0.5 rounded-full flex items-center shadow-sm border border-gray-200">
                            <span className="text-[10px]">{normalizedMedia.type === 'video' ? '🎬' : '🖼️'}</span>
                          </div>
                        );
                      })()}
                      
                      {/* Content preview overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs font-medium truncate">
                          {post.postContent}
                        </p>
                      </div>
                    </div>
                    
                    {/* Debug Mode - Show Media Structure */}
                    {debugMode && (
                      <div className="mt-2">
                        <MediaStructureDebug 
                          media={post.postMediaUrl && post.postMediaUrl.trim() !== '' ? { url: post.postMediaUrl, type: 'image' as const } : null} 
                          postId={post.postId}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mb-3 text-xs text-gray-500 italic">{t('posts.noPosts', 'Aucun post pour ce persona')}</div>
            )}
            {hoveredPost && (
              <div
                ref={previewRef}
                className="fixed pointer-events-none z-50"
                style={{ left: getOptimalPosition().x, top: getOptimalPosition().y }}
              >
                <PostPreview post={mapRecentPostToPost(hoveredPost)} />
              </div>
            )}
          </div>
        </>

        {/* Divider */}
        <hr className="my-4 border-t border-gray-200" />

        {/* Motivations & Goals Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">{t('personas.form.tabs.reasons', 'Reasons')}</h4>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🎯</span>
                <h4 className="text-sm font-medium text-gray-700">{t('personas.form.goals')}</h4>
              </div>
              <p className="text-sm text-gray-600 line-clamp-3">{formatField(goals)}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🚀</span>
                <h4 className="text-sm font-medium text-gray-700">{t('personas.form.motivations')}</h4>
              </div>
              <p className="text-sm text-gray-600 line-clamp-3">{formatField(motivations)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">😣</span>
                <h4 className="text-sm font-medium text-gray-700">{t('personas.form.problems')}</h4>
              </div>
              <p className="text-sm text-gray-600 line-clamp-3">{formatField(problems)}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🔥</span>
                <h4 className="text-sm font-medium text-gray-700">{t('personas.form.challenges')}</h4>
              </div>
              <p className="text-sm text-gray-600 line-clamp-3">{formatField(challenges)}</p>
            </div>
          </div>
        </div>

        {/* Content Format Section */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-gray-400" />
            <h4 className="text-sm font-medium text-gray-700">{t('personas.form.contentFormat', 'Content Format')}</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {(formatLength || []).map((format, index) => {
              // Map format to emoji
              const getFormatEmoji = (formatName: string) => {
                switch (formatName) {
                  case 'Short/Reel':
                    return '📱';
                  case 'Long/Classic':
                    return '📺';
                  case 'Story/Ephemeral':
                    return '⚡';
                  case 'Live/Stream':
                    return '🎥';
                  default:
                    return '📄';
                }
              };

              return (
                <span
                  key={index}
                  className="px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs flex items-center gap-1"
                >
                  <span>{getFormatEmoji(format)}</span>
                  {format}
                </span>
              );
            })}
          </div>
        </div>
        
        {/* Behavior Section */}
        {(buying_behavior || brand_affinity || quote) && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {t('personas.form.tabs.behavior', 'Behavior')}
            </h4>
            
            {/* Quote is now displayed at the top right */}
            
            {buying_behavior && (
              <div className="mb-2">
                <div className="flex items-start gap-2">
                  <span className="text-lg">🛒</span>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600">{t('personas.form.buyingBehavior')}</h5>
                    <p className="text-sm text-gray-700 line-clamp-2">{buying_behavior}</p>
                  </div>
                </div>
              </div>
            )}
            
            {brand_affinity && (
              <div>
                <div className="flex items-start gap-2">
                  <span className="text-lg">🏷️</span>
                  <div>
                    <h5 className="text-xs font-medium text-gray-600">{t('personas.form.brandAffinity')}</h5>
                    <p className="text-sm text-gray-700 line-clamp-2">{brand_affinity}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreatePostModalOpen}
        onClose={() => setIsCreatePostModalOpen(false)}
        onCreateWithAI={handleCreatePostWithAI}
        onCreateManually={handleCreatePostManually}
      />
    </div>
  );
};